/**
 * Deployment Request Detail Drawer (S10 + S11).
 *
 * Right-slide panel. Shows:
 *   - Header: DR id, current stage badge, risk pill, owner, SLA, opportunity link
 *   - Timeline: every stage on the path with state (complete / active / pending / skipped / rejected)
 *   - Approval checklist (active during stage 4): per-approver state
 *   - Audit trail: every decision recorded against this request
 *   - Footer actions (S11): Request changes · Reject · Approve / Deploy
 */
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  X,
  CheckCircle2,
  Circle,
  CircleDot,
  XCircle,
  AlertTriangle,
  Send,
  Rocket,
  ArrowLeft,
  Link as LinkIcon,
  Clock,
  User as UserIcon,
} from "lucide-react";
import {
  STAGES,
  buildTimeline,
  approvalChecklist,
  stageLabel,
  isTerminal,
} from "@/lib/deploymentWorkflow";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { can, readOnlyReason } from "@/lib/rbac";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { StageBadge, RiskPill } from "./components/DeploymentPrimitives";

export default function DeploymentRequestDetail({ request, onClose }) {
  const { user } = useAuth();
  const { advanceDeploymentStage } = useTenantData();
  const navigate = useNavigate();

  useEffect(() => {
    if (!request) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [request, onClose]);

  if (!request) return null;

  const timeline = buildTimeline(request);
  const checklist = approvalChecklist(request);
  const terminal = isTerminal(request);
  const canApproveCurrentStage = can(user?.id, `approve:stage-${request.stage}`);
  const denialReason = readOnlyReason(user?.id);

  const onDecision = async (decision) => {
    try {
      const result = await advanceDeploymentStage(request, decision, user);
      const spawned = result?.spawnedAgent;
      if (spawned) {
        toast.success(`Agent ${spawned.id} is LIVE`, {
          description: `${spawned.name} · bound to ${spawned.policiesApplied} policies · frameworks: ${(spawned.frameworks || []).slice(0, 2).join(", ")}`,
          action: {
            label: "Open agent",
            onClick: () => {
              onClose();
              navigate(`/govern/agents/${spawned.id}`);
            },
          },
          duration: 10000,
        });
      } else if (decision === "approve" && result?.request?.stage) {
        toast.message(`Advanced to stage ${result.request.stage}`, {
          description: stageLabel(result.request.stage),
          duration: 2200,
        });
      } else if (decision === "reject") {
        toast.warning("Request rejected", { description: `${request.id} closed.` });
      } else if (decision === "changes") {
        toast.message("Changes requested", { description: `${request.id} returned to previous stage.` });
      }
    } catch (e) {
      toast.error("Action failed", { description: e?.message || "Unknown error" });
      logger.warn("[deployment] decision failed", e);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      data-testid="deploy-detail-drawer"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-primary/40 backdrop-blur-[2px]"
        data-testid="deploy-detail-backdrop"
      />
      <aside
        className="absolute right-0 top-0 bottom-0 w-full sm:w-[640px] bg-raised shadow-e4 border-l border-border-subtle flex flex-col anim-slide-right"
      >
        <Header request={request} onClose={onClose} />
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          <CurrentStageCard request={request} />
          <TimelineSection timeline={timeline} />
          {request.stage === 4 && !terminal && (
            <ApprovalChecklistSection checklist={checklist} />
          )}
          <AuditTrailSection request={request} />
        </div>
        <Footer
          request={request}
          terminal={terminal}
          onDecision={onDecision}
          canApprove={canApproveCurrentStage}
          denialReason={denialReason}
        />
      </aside>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Header({ request, onClose }) {
  return (
    <div className="border-b border-border-subtle px-5 pt-4 pb-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-meta font-mono text-ink-secondary tabular-nums" data-testid="deploy-detail-id">
              {request.id}
            </span>
            <StageBadge stageId={request.stage} terminal={request.terminal} status={request.status} />
            <RiskPill risk={request.risk} />
          </div>
          <h2 className="mt-1 text-h2 text-ink-primary" data-testid="deploy-detail-title">
            {request.agentName}
          </h2>
          <p className="mt-1 text-meta text-ink-secondary flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <UserIcon size={11} /> {request.requestor}
            </span>
            <span>· {request.department}</span>
            <span>· {request.businessUnit}</span>
            {request.opportunityId && (
              <span className="inline-flex items-center gap-1" data-testid="deploy-detail-oppid">
                <LinkIcon size={11} /> {request.opportunityId}
              </span>
            )}
            {request.slaDue && request.slaDue !== "—" && (
              <span className="inline-flex items-center gap-1">
                <Clock size={11} /> SLA {request.slaDue}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close drawer"
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-secondary hover:bg-inset transition-colors"
          data-testid="deploy-detail-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function CurrentStageCard({ request }) {
  if (request.terminal && request.status === "rejected") {
    return (
      <div
        className="surface-card p-4 border-l-[3px] border-l-risk-high"
        data-testid="deploy-detail-current-stage"
      >
        <p className="eyebrow text-risk-high">Terminal — Rejected</p>
        <p className="mt-1 text-h3 text-ink-primary">
          Rejected at stage {request.stage}: {stageLabel(request.stage)}
        </p>
        <p className="mt-1 text-meta text-ink-secondary">
          This request is closed. A new opportunity submission is required to re-open.
        </p>
      </div>
    );
  }
  if (request.terminal) {
    return (
      <div
        className="surface-card p-4 border-l-[3px] border-l-risk-low"
        data-testid="deploy-detail-current-stage"
      >
        <p className="eyebrow text-risk-low">Deployed</p>
        <p className="mt-1 text-h3 text-ink-primary">Live and under continuous governance.</p>
        <p className="mt-1 text-meta text-ink-secondary">
          See Performance & Drift or the Agent Inventory for runtime detail.
        </p>
      </div>
    );
  }
  const stage = STAGES.find((s) => s.id === request.stage);
  return (
    <div
      className="surface-card p-4 border-l-[3px] border-l-tralenex-600"
      data-testid="deploy-detail-current-stage"
    >
      <p className="eyebrow">Current stage</p>
      <p className="mt-1 text-h3 text-ink-primary">
        {stage.id}/7 · {stage.label}
      </p>
      <p className="mt-1 text-meta text-ink-secondary">{stage.description}</p>
      <p className="mt-2 text-meta">
        <span className="text-ink-secondary">Next action by </span>
        <span className="text-ink-primary font-semibold">{stage.owner}</span>
      </p>
    </div>
  );
}

function TimelineSection({ timeline }) {
  return (
    <section data-testid="deploy-detail-timeline">
      <p className="eyebrow mb-3">Governance checkpoints</p>
      <ol className="space-y-2.5">
        {timeline.map((s) => (
          <TimelineRow key={s.id} stage={s} />
        ))}
      </ol>
    </section>
  );
}

function TimelineRow({ stage }) {
  const stateIcon = {
    complete: <CheckCircle2 size={15} className="text-risk-low" />,
    active: <CircleDot size={15} className="text-gold-600" />,
    pending: <Circle size={15} className="text-ink-disabled" />,
    skipped: <Circle size={15} className="text-ink-disabled opacity-50" />,
    rejected: <XCircle size={15} className="text-risk-high" />,
  }[stage.state];

  const labelTone = {
    complete: "text-ink-primary",
    active: "text-ink-primary font-semibold",
    pending: "text-ink-secondary",
    skipped: "text-ink-disabled italic",
    rejected: "text-risk-high font-semibold",
  }[stage.state];

  return (
    <li
      className="flex items-start gap-3"
      data-testid={`deploy-timeline-stage-${stage.id}`}
      data-state={stage.state}
    >
      <span className="mt-0.5 shrink-0">{stateIcon}</span>
      <div className="flex-1 min-w-0">
        <p className={cn("text-sm leading-tight", labelTone)}>
          <span className="font-mono text-meta text-ink-disabled mr-1 tabular-nums">
            {stage.id}.
          </span>
          {stage.label}
          {stage.state === "skipped" && (
            <span className="ml-1 text-meta text-ink-disabled">(skipped — low risk)</span>
          )}
        </p>
        <p className="text-meta text-ink-secondary">{stage.description}</p>
        <p className="mt-0.5 text-meta text-ink-disabled">Owner: {stage.owner}</p>
      </div>
    </li>
  );
}

function ApprovalStateIcon({ state }) {
  if (state === "complete") return <CheckCircle2 size={14} className="text-risk-low" />;
  if (state === "active")   return <CircleDot   size={14} className="text-gold-600" />;
  return <Circle size={14} className="text-ink-disabled" />;
}

function approverNameClass(state) {
  if (state === "active")   return "flex-1 text-ink-primary font-semibold";
  if (state === "complete") return "flex-1 text-ink-primary";
  return "flex-1 text-ink-secondary";
}

function decisionToneClass(decision) {
  if (decision === "rejected")          return "text-risk-high";
  if (decision === "changes-requested") return "text-risk-med";
  return "text-ink-secondary";
}

function ApprovalChecklistSection({ checklist }) {
  return (
    <section data-testid="deploy-detail-approvals">
      <p className="eyebrow mb-3">Required approvals — stage 4</p>
      <ol className="space-y-2">
        {checklist.map((a, i) => (
          <li key={a.approver} className="flex items-center gap-2.5 text-sm" data-testid={`deploy-approval-${i}`}>
            <span className="shrink-0"><ApprovalStateIcon state={a.state} /></span>
            <span className={approverNameClass(a.state)}>{a.approver}</span>
            <span className="text-meta text-ink-disabled uppercase tracking-[0.06em]">
              {a.state}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AuditTrailSection({ request }) {
  const entries = request.decisions || [];
  return (
    <section data-testid="deploy-detail-audit">
      <p className="eyebrow mb-3">Audit trail · {entries.length} {entries.length === 1 ? "entry" : "entries"}</p>
      {entries.length === 0 ? (
        <p className="text-meta text-ink-secondary">No audit entries yet.</p>
      ) : (
        <ol className="border-l border-border-subtle ml-2 space-y-3 pl-4">
          {[...entries].reverse().map((d, i) => (
            <li
              key={`${d.ts}-${d.actor}-${d.stage}-${d.decision}`}
              className="relative"
              data-testid={`deploy-audit-entry-${i}`}
            >
              <span className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-tralenex-600 ring-2 ring-raised" />
              <p className="text-sm text-ink-primary">
                <span className="font-semibold">{d.actor}</span>{" "}
                <span className={decisionToneClass(d.decision)}>
                  {labelForDecision(d.decision)}
                </span>{" "}
                at stage {d.stage} — {stageLabel(d.stage)}
              </p>
              <p className="text-meta text-ink-disabled font-mono tabular-nums">{d.ts}</p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function labelForDecision(d) {
  if (d === "approved") return "approved";
  if (d === "rejected") return "rejected";
  if (d === "changes-requested") return "requested changes";
  return d;
}

function Footer({ request, terminal, onDecision, canApprove, denialReason }) {
  if (terminal) {
    return (
      <div className="border-t border-border-subtle px-5 py-3 bg-inset/30 text-meta text-ink-secondary" data-testid="deploy-detail-footer">
        <AlertTriangle size={13} className="inline mr-1 text-ink-disabled" />
        This request is in a terminal state. No further decisions can be taken.
      </div>
    );
  }
  if (!canApprove) {
    return (
      <div
        className="border-t border-border-subtle px-5 py-3 bg-inset/30 text-meta text-ink-secondary"
        data-testid="deploy-detail-footer"
      >
        <span
          className="inline-flex items-center gap-1.5"
          data-testid="deploy-action-locked"
          title={denialReason}
        >
          <AlertTriangle size={13} className="text-ink-disabled" />
          You are not authorised to act at stage {request.stage}. {denialReason}
        </span>
      </div>
    );
  }
  const isDeploymentStage = request.stage === 6;
  return (
    <div className="border-t border-border-subtle px-5 py-3 bg-inset/30 flex items-center gap-2 flex-wrap" data-testid="deploy-detail-footer">
      <button
        type="button"
        onClick={() => onDecision("changes")}
        className="h-9 px-3 rounded-md text-sm font-medium text-ink-secondary hover:bg-inset transition-colors inline-flex items-center gap-1.5"
        data-testid="deploy-action-changes"
      >
        <ArrowLeft size={13} /> Request changes
      </button>
      <button
        type="button"
        onClick={() => onDecision("reject")}
        className="h-9 px-3 rounded-md text-sm font-medium text-risk-high hover:bg-risk-high/10 transition-colors inline-flex items-center gap-1.5"
        data-testid="deploy-action-reject"
      >
        <XCircle size={13} /> Reject
      </button>
      <span className="flex-1" />
      <button
        type="button"
        onClick={() => onDecision(isDeploymentStage ? "deploy" : "approve")}
        className="h-9 px-4 rounded-md text-sm font-semibold bg-tralenex-600 text-white hover:bg-tralenex-700 inline-flex items-center gap-2"
        data-testid="deploy-action-approve"
      >
        {isDeploymentStage ? (
          <>
            <Rocket size={13} /> Deploy
          </>
        ) : (
          <>
            <Send size={13} /> Approve · advance to stage {request.stage + 1}
          </>
        )}
      </button>
    </div>
  );
}
